'use client';
import { ProductStatusText } from '@eliadmin/core/src/components/product/product-status-text';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { PRODUCT_CONFIG } from '@eliadmin/core/src/constants/product-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React from 'react';

export function ProductDetailPrice() {
  const { t } = useTranslations();
  const settings = useGlobalStore(state => state.templateSettings);
  const product = useVariantsStore(state => state.product);
  const hasSpecialProductStatus = product.status === 'ended' || product.status === 'arrival';
  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const variants = useVariantsStore(state => state.product.variants);
  const selectedValues = useVariantsStore(state => state.selectedValues);
  const selectedServices = useVariantsStore(state => state.selectedServices);

  const me = useCustomerStore(state => state.me);

  const getServicesPrice = (vat = false) => {
    let price = 0;
    selectedServices.forEach(service => {
      price += vat
        ? parseFloat(service.price_with_vat.toString())
        : parseFloat(service.price.toString());
    });
    return price;
  };

  const desiredObject = variants?.length ? selectedVariant : product;
  const priceWithoutVatFirst = settings?.product_detail?.price_without_vat_first?.enabled;
  const priceDisplay = getProductPriceDisplay(desiredObject ?? product, {
    isWholesaler: me?.customer.is_wholesaler,
    priceWithoutVatFirst,
  });

  //   const servicesPrice = selectedServices.reduce(
  //     (acc, service) => acc + roundPrice(service.price_with_vat, currency),
  //     0,
  //   );
  const unit = settings.product_detail?.show_unit?.enabled ? ` / ${product.unit}` : '';
  const mainPriceUsesVat = !priceWithoutVatFirst;
  const mainPrice =
    priceDisplay.current == null
      ? undefined
      : priceDisplay.current + getServicesPrice(mainPriceUsesVat);
  const mainPriceBeforeDiscount =
    priceDisplay.beforeDiscount == null
      ? undefined
      : priceDisplay.beforeDiscount + getServicesPrice(mainPriceUsesVat);
  const secondaryPrice =
    priceDisplay.secondary == null
      ? undefined
      : priceDisplay.secondary + getServicesPrice(priceDisplay.secondaryIncludesVat);
  const mocPrice =
    priceDisplay.mocPrice == null
      ? undefined
      : priceDisplay.mocPrice + getServicesPrice(mainPriceUsesVat);

  const isDiscounted = priceDisplay.showPreviousPrice;

  const renderErrorMessage = () => {
    if (selectedValues.length === product?.attributes?.length) {
      return (
        <span className={cn('text-2xl font-semibold text-destructive-foreground', '')}>
          {t('text495')}
        </span>
      );
    }
    if (product?.attributes?.length && selectedValues.length < product?.attributes?.length) {
      return (
        <span className={cn('text-2xl font-semibold text-destructive-foreground', '')}>
          {t('text496')}
        </span>
      );
    }
  };

  if (hasSpecialProductStatus) {
    return <ProductStatusText status={product.status} />;
  }

  if ((!variants?.length || selectedVariant) && priceDisplay.priceOnRequest) {
    return null;
  }

  return (
    <div className={cn('flex flex-col items-start gap-2', 'ea-product-detail-price')}>
      {!selectedVariant && !!variants?.length ? (
        renderErrorMessage()
      ) : (
        <>
          <div className={cn('flex flex-row items-center', 'ea-product-detail-price__container')}>
            <div>
              {priceDisplay.showMocVocPrices && mocPrice != null ? (
                <div className="flex">
                  {`${t('text1926')} ${formatPrice(mocPrice, currency, currencySettings)}${unit}`}
                </div>
              ) : null}
              <span
                className={cn(
                  'text-2xl font-semibold lg:text-3xl',
                  'ea-product-detail-price__after-discount',
                  isDiscounted ? 'text-[#B80900]' : 'text-foreground',
                )}
              >
                <strong className="text-2xl font-semibold lg:text-3xl">{`${formatPrice(
                  mainPrice,
                  currency,
                  currencySettings,
                )}${unit}`}</strong>
              </span>
            </div>
            {isDiscounted && (
              <span
                className={cn(
                  'ml-2 text-lg text-foreground sm:text-xl lg:text-2xl',
                  'ea-product-detail-price__with-vat',
                )}
              >
                <del className="text-lg text-foreground sm:text-xl lg:text-2xl">{`${formatPrice(
                  mainPriceBeforeDiscount,
                  currency,
                  currencySettings,
                )}${unit}`}</del>
              </span>
            )}
          </div>
          {settings.product_detail?.show_price_without_vat?.enabled && (
            <span
              className={cn(
                'text-lg font-normal text-[#666464] sm:text-xl',
                'ea-product-detail-price__without-vat',
              )}
            >
              {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
              {formatPrice(secondaryPrice, currency, currencySettings)}{' '}
              {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')} {unit}
            </span>
          )}
        </>
      )}
    </div>
  );
}
